import { inject } from "@angular/core";
import { ResolveFn } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { isChatUnavailableError } from "@app/shared/general/chat/helpers/chat-error.helper";
import { IResourceChatIdentity } from "@app/shared/general/resource-chat/interfaces/resource-chat.interfaces";
import { ResourceChatRegistryService } from "@app/shared/general/resource-chat/services/resource-chat-registry.service";
import { IMentionDiscussionAccess } from "../interfaces/mention-discussion.interfaces";
import { hasMentionDiscussionAccess } from "../helpers/mention-discussion-access.helper";
import { MentionDiscussionApiService } from "../services/mention-discussion-api.service";

/** Only scoped data crosses the generic page's route boundary. */
export interface IMentionDiscussionEntry {
  resource: IResourceChatIdentity;
  access: IMentionDiscussionAccess | null;
  state: "ready" | "unavailable" | "error";
}

/** Reauthorizes direct links and reloads without fetching protected resource metadata. */
export const mentionDiscussionAccessResolver: ResolveFn<IMentionDiscussionEntry> = async route => {
  const api = inject(MentionDiscussionApiService);
  const registry = inject(ResourceChatRegistryService);
  const resource = { resourceName: route.paramMap.get("resourceName") ?? "", resourceId: route.paramMap.get("resourceId") ?? "" };
  const messageId = route.paramMap.get("messageId") ?? "";
  const unavailable: IMentionDiscussionEntry = { resource, access: null, state: "unavailable" };
  if (!registry.get(resource.resourceName) || !/^[a-f\d]{24}$/i.test(resource.resourceId) || !/^[a-f\d]{24}$/i.test(messageId)) return unavailable;
  try {
    const access = await firstValueFrom(api.access(resource, messageId));
    return hasMentionDiscussionAccess(access, messageId) ? { resource, access, state: "ready" } : unavailable;
  } catch (error) {
    return isChatUnavailableError(error) ? unavailable : { resource, access: null, state: "error" };
  }
};
